import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip
} from '@mui/material';

/**
 * RiskHeatmap Component
 * Grid heatmap showing vendor distribution by tier and trust score classification.
 * Backend returns: { tiers: [1,2,3], classifications: [...], data: [{tier, classification, count, vendors}] }
 */
export const RiskHeatmap = ({ data }) => {
  const { t } = useTranslation(['dashboard']);

  // Handle null, undefined, or invalid data
  if (!data || !data.tiers || !data.classifications || !data.data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('dashboard:riskHeatmap.noDataTitle')}</Typography>
          <Typography color="textSecondary">{t('dashboard:riskHeatmap.noData')}</Typography>
        </CardContent>
      </Card>
    );
  }

  const { tiers, classifications } = data;

  const getCell = (tier, classification) => {
    return data.data.find(d => d.tier === tier && d.classification === classification);
  };

  const getCellColor = (classification, count) => {
    if (count === 0) return '#f5f5f5';
    switch (classification) {
      case 'Audit Ready': return '#c8e6c9';
      case 'Risky': return '#fff9c4';
      case 'Unacceptable': return '#ffcdd2';
      default: return '#f5f5f5';
    }
  };

  const getCellBorderColor = (classification) => {
    switch (classification) {
      case 'Audit Ready': return '#4caf50';
      case 'Risky': return '#ff9800';
      case 'Unacceptable': return '#f44336';
      default: return '#e0e0e0';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 1: return t('dashboard:riskHeatmap.tier1');
      case 2: return t('dashboard:riskHeatmap.tier2');
      case 3: return t('dashboard:riskHeatmap.tier3');
      default: return `Tier ${tier}`;
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('dashboard:riskHeatmap.title')}</Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          {t('dashboard:riskHeatmap.subtitle')}
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `120px repeat(${classifications.length}, 1fr)`,
              gap: 1,
              minWidth: 400,
            }}
          >
            {/* Header row */}
            <Box />
            {classifications.map((cls) => (
              <Box key={cls} sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" fontWeight="bold">
                  {cls}
                </Typography>
              </Box>
            ))}

            {/* Data rows */}
            {tiers.map((tier) => (
              <React.Fragment key={tier}>
                <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                  <Typography variant="caption" fontWeight="bold">
                    {getTierLabel(tier)}
                  </Typography>
                </Box>
                {classifications.map((cls) => {
                  const cell = getCell(tier, cls);
                  const count = cell?.count || 0;
                  const vendors = cell?.vendors || [];

                  return (
                    <Tooltip
                      key={`${tier}-${cls}`}
                      title={
                        count > 0
                          ? vendors.join(', ')
                          : t('dashboard:riskHeatmap.noVendors')
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          bgcolor: getCellColor(cls, count),
                          border: `2px solid ${count > 0 ? getCellBorderColor(cls) : '#e0e0e0'}`,
                          borderRadius: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 2,
                          px: 1,
                          minHeight: 80,
                          cursor: count > 0 ? 'pointer' : 'default',
                          transition: 'transform 0.2s',
                          '&:hover': count > 0 ? { transform: 'scale(1.02)' } : {},
                        }}
                      >
                        <Typography variant="h4" fontWeight="bold">
                          {count}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {count === 1 ? t('dashboard:riskHeatmap.vendor') : t('dashboard:riskHeatmap.vendors')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Typography variant="caption" color="textSecondary" display="block" mt={2}>
          {t('dashboard:riskHeatmap.hoverHint')}
        </Typography>
      </CardContent>
    </Card>
  );
};
